import { AxiosResponse } from 'axios';

import {
  Configuration,
  CreateLocationDTO,
  CreateClinicianDTO,
  DefaultApi as WeaverApi,
  PaginatedFeatureGroupDTO,
  PaginatedLocationsDTO,
  PatientWithAccessRightDTO,
  UpdateClinicianDTO,
  UpdateLocationDTO,
  UpdatePrescriptionRequestDTO,
  CreatePatientDTO,
  UpdatePatientDTO,
  ClinicianDTO,
  ReprescribeResponseDto,
  ReprescribeRequestDto,
  PaginatedPatientsDTO
} from '@peartherapeutics/weaver-api-client';

import { Config } from 'utils/config/config';
import { loadAuthToken } from 'utils/tokenStorage';
import { handleExpiredToken } from 'utils/session';

/**
 * Singleton to ensure there is a single instantiation of WeaverAPI
 */
export class WeaverApiClientInstance {
  private static instance: WeaverApi | null;

  private static unauthenticatedInstance: WeaverApi | null;

  public static get(): WeaverApi {
    if (!this.instance) {
      this.instance = new WeaverApi(
        new Configuration({
          apiKey: `JWT ${loadAuthToken()}`,
          basePath: Config.WEAVER_API_URL,
        }),
      );
    }
    return this.instance;
  }

  public static getUnauthenticatedInstance(): WeaverApi {
    this.unauthenticatedInstance = new WeaverApi(
      new Configuration({
        basePath: Config.WEAVER_API_URL,
      }),
    );
    return this.unauthenticatedInstance;
  }

  private static sendRequest<T>(request: any): Promise<AxiosResponse<T>> {
    return new Promise((resolve, reject) => {
      request
        .then((data: AxiosResponse<T>) => {
          resolve(data);
        })
        .catch((error: any) => {
          if (error.response?.status === 401) {
            handleExpiredToken();
          }

          reject(error);
        });
    });
  }

  public static getUserProfile = async (): Promise<
    AxiosResponse<UserProfileDTO>
  > => {
    return WeaverApiClientInstance.sendRequest(
      WeaverApiClientInstance.get().getUserProfile(),
    );
  };

  public static updateLocation = async (
    updateLocationDTO: UpdateLocationDTO,
    options?: any,
  ): Promise<AxiosResponse<LocationDTO>> => {
    return WeaverApiClientInstance.sendRequest<LocationDTO>(
      WeaverApiClientInstance.get().updateLocation(updateLocationDTO, options),
    );
  };

  public static getFeatureGroups = async (
    options?: any,
  ): Promise<AxiosResponse<PaginatedFeatureGroupDTO>> => {
    return WeaverApiClientInstance.sendRequest<PaginatedFeatureGroupDTO>(
      WeaverApiClientInstance.get().getFeatureGroups(options),
    );
  };

  public static updatePatient = async (
    updatePatient: UpdatePatientDTO,
    options?: any,
  ): Promise<AxiosResponse<PatientWithAccessRightDTO>> => {
    return WeaverApiClientInstance.sendRequest<PatientWithAccessRightDTO>(
      WeaverApiClientInstance.get().updatePatient(
        updatePatient.id!,
        updatePatient,
        options,
      ),
    );
  };

  public static createClinician = async (
    createClinicianDTO: CreateClinicianDTO,
    options?: any,
  ): Promise<AxiosResponse<boolean>> => {
    return WeaverApiClientInstance.sendRequest<boolean>(
      WeaverApiClientInstance.get().createClinician(
        createClinicianDTO,
        options,
      ),
    );
  };

  public static createLocation = async (
    createLocationDTO: CreateLocationDTO,
    options?: any,
  ): Promise<AxiosResponse<LocationDTO>> => {
    return WeaverApiClientInstance.sendRequest<LocationDTO>(
      WeaverApiClientInstance.get().createLocation(createLocationDTO, options),
    );
  };

  public static getGiftCardVendors = async (
    options?: any,
  ): Promise<AxiosResponse<GiftCardVendorDTO[]>> => {
    return WeaverApiClientInstance.sendRequest<GiftCardVendorDTO[]>(
      WeaverApiClientInstance.get().getGiftCardVendors(options),
    );
  };

  public static getAllActiveCliniciansForLocation = async (
    locationId: string,
    options?: any,
  ): Promise<AxiosResponse<ClinicianDTO[]>> => {
    return WeaverApiClientInstance.sendRequest<ClinicianDTO[]>(
      WeaverApiClientInstance.get().getAllActiveCliniciansForLocation(
        locationId,
        options,
      ),
    );
  };

  public static getAllFeatureGroups = async (
    options?: any,
  ): Promise<AxiosResponse<FeatureGroupDTO[]>> => {
    return WeaverApiClientInstance.sendRequest<FeatureGroupDTO[]>(
      WeaverApiClientInstance.get().getAllFeatureGroups(options),
    );
  };

  public static getPrograms = async (
    options?: any,
  ): Promise<AxiosResponse<ProgramDTO[]>> => {
    return WeaverApiClientInstance.sendRequest<ProgramDTO[]>(
      WeaverApiClientInstance.get().getPrograms(options),
    );
  };

  public static updatePrescription = async (
    updatePrescriptionRequestDTO: UpdatePrescriptionRequestDTO,
    options?: any,
  ): Promise<AxiosResponse<UpdatePrescriptionResponseDTO>> => {
    return WeaverApiClientInstance.sendRequest<UpdatePrescriptionResponseDTO>(
      WeaverApiClientInstance.get().updatePrescription(
        updatePrescriptionRequestDTO,
        options,
      ),
    );
  };

  public static getAllCliniciansForLocation = async (
    locationId: string,
    options?: any,
  ): Promise<AxiosResponse<ClinicianDTO[]>> => {
    return WeaverApiClientInstance.sendRequest<ClinicianDTO[]>(
      WeaverApiClientInstance.get().getAllCliniciansForLocation(
        locationId,
        options,
      ),
    );
  };

  public static updateClinician = async (
    updateClinicianDTO: UpdateClinicianDTO,
    options?: any,
  ): Promise<AxiosResponse<boolean>> => {
    return WeaverApiClientInstance.sendRequest<boolean>(
      WeaverApiClientInstance.get().updateClinician(
        updateClinicianDTO,
        options,
      ),
    );
  };

  public static getLocations = async (
    limit?: number,
    nextCursor?: string,
    prevCursor?: string,
    sort?: string,
    sortBy?: string,
    options?: any,
  ): Promise<AxiosResponse<PaginatedLocationsDTO>> => {
    return WeaverApiClientInstance.sendRequest<PaginatedLocationsDTO>(
      WeaverApiClientInstance.get().getLocations(
        limit,
        nextCursor,
        prevCursor,
        sort,
        sortBy,
        options,
      ),
    );
  };

  public static getAllLocations = async (): Promise<
    AxiosResponse<LocationDTO[]>
  > => {
    return WeaverApiClientInstance.sendRequest<LocationDTO[]>(
      WeaverApiClientInstance.get().getAllLocations(),
    );
  };

  public static createPatient = async (
    req: CreatePatientDTO,
  ): Promise<AxiosResponse<PatientWithAccessRightDTO>> => {
    return WeaverApiClientInstance.sendRequest<PatientWithAccessRightDTO>(
      WeaverApiClientInstance.get().createPatient(req),
    );
  };

  public static getPatients = async (
    locationId?: string,
    program?: string,
  ): Promise<PatientWithAccessRightDTO[]> => {
    const { data } = await WeaverApiClientInstance.get().getPatients(
      program,
      locationId,
    );
    return data;
  };

  public static rePrescribe = async (
    request: ReprescribeRequestDto,
  ): Promise<ReprescribeResponseDto> => {
    const { data } = await WeaverApiClientInstance.get().rePrescribe(request);
    return data;
  };

  public static reset(): void {
    this.instance = null;
  };

  public static getPaginatedPatients = async (
  limit?: number,
  nextCursor?: string,
  prevCursor?: string,
  sort: 'asc' | 'desc' = 'asc',
  sortBy: string = 'name',
  locationId?: string,
  program?: string,
  search?:string,
  options?: any
): Promise<AxiosResponse<PaginatedPatientsDTO>> => {
  return WeaverApiClientInstance.sendRequest<PaginatedPatientsDTO>(
    WeaverApiClientInstance.get().getPaginatedPatients(
      program,       
      locationId,   
      search, 
      limit,
      nextCursor,
      prevCursor,
      sort,
      sortBy,
      options
    )
  );
};
  
}
